import React, { useState, useEffect, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import ExportToCsvButton from "./ExportToCsvButton";
import { fetchSpacRiskData, fetchSpacLiquidationSummary } from "../services/Axios";
import { formatCurrency } from "../utils/dataCalculations";
import StatItem from "./ui/StatItem";
import { useNav } from "../context/NavContext";
import {
  WEIGHTED_AVG_DAYS_LABEL,
  selectLiquidationMetric,
} from "../utils/spacLiquidationSummary";

const SpacRisk = () => {
  const theme = useTheme();
  const NAV_NUMBER = useNav();
  const [spacRiskData, setSpacRiskData] = useState([]);
  const [liquidationSummary, setLiquidationSummary] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchSpacRiskData();
        if (response?.data) {
          setSpacRiskData(response.data);
        }
      } catch (error) {
        console.error("Error fetching SPAC risk data:", error);
      }

      try {
        const liquidation = await fetchSpacLiquidationSummary();
        setLiquidationSummary(liquidation);
      } catch (error) {
        console.error("Error fetching SPAC liquidation summary:", error);
      }
    };

    fetchData();
  }, []);

  // Calculate summary statistics
  const calculateSummary = () => {
    if (!spacRiskData.length)
      return {
        totalLMV: 0,
        totalSMV: 0,
        netMarketValue: 0,
        totalRiskToZero: 0,
        totalRiskTo50: 0,
        totalRiskTo100: 0,
        totalRiskTo50pctDrop: 0,
        totalRiskTo75pctDrop: 0,
      };

    return spacRiskData.reduce(
      (acc, position) => {
        // Market values
        acc.totalLMV += Number(position.LMV) || 0;
        acc.totalSMV += Number(position.SMV) || 0;
        acc.netMarketValue = acc.totalLMV + acc.totalSMV; // SMV is already negative

        // Risk metrics
        acc.totalRiskToZero += Number(position.risk_to_zero_bps_spread) || 0;
        acc.totalRiskTo50 += Number(position.risk_to_50_bps_spread) || 0;
        acc.totalRiskTo100 += Number(position.risk_to_100_bps_spread) || 0;
        acc.totalRiskTo50pctDrop +=
          Number(position.risk_to_50pct_drop_in_wts_rts) || 0;
        acc.totalRiskTo75pctDrop +=
          Number(position.risk_to_75pct_drop_in_wts_rts) || 0;
        return acc;
      },
      {
        totalLMV: 0,
        totalSMV: 0,
        netMarketValue: 0,
        totalRiskToZero: 0,
        totalRiskTo50: 0,
        totalRiskTo100: 0,
        totalRiskTo50pctDrop: 0,
        totalRiskTo75pctDrop: 0,
      }
    );
  };

  const summary = calculateSummary();

  const weightedAvgDaysToLiquidation = selectLiquidationMetric(
    liquidationSummary,
    WEIGHTED_AVG_DAYS_LABEL
  );

  // Custom Table Component
  const CustomTable = ({ columns, data, defaultSort }) => {
    const [sorting, setSorting] = useState(defaultSort ? [defaultSort] : []);

    const table = useReactTable({
      data,
      columns,
      state: {
        sorting,
      },
      onSortingChange: setSorting,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
    });

    return (
      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: "transparent",
          backgroundImage: "none",
          boxShadow: "none",
          borderRadius: 0,
          maxHeight: "calc(100vh - 280px)", // Adjust this value based on your header/footer heights
          overflow: "auto",
          "& .MuiTableHead-root": {
            position: "sticky",
            top: 0,
            zIndex: 2,
            backgroundColor: "#fff",
          },
        }}
      >
        <Table
          stickyHeader
          sx={{
            "& .MuiTableCell-root": {
              padding: "6px 4px",
              borderBottom: "1px solid rgba(224, 224, 224, 0.3)",
              fontSize: "0.75rem",
            },
            "& .MuiTableRow-root:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
            "& .MuiTableCell-stickyHeader": {
              backgroundColor: "#fff",
              fontWeight: 600,
            },
          }}
        >
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    sx={{
                      cursor: "pointer",
                      fontWeight: 600,
                      backgroundColor: "rgba(0, 0, 0, 0.02)",
                      borderBottom: "2px solid rgba(224, 224, 224, 0.5)",
                      color: "#1a237e",

                      letterSpacing: "0.5px",
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                      textAlign: header.column.columnDef.align || "left",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {header.column.getIsSorted() && (
                      <Box component="span" sx={{ ml: 1 }}>
                        {header.column.getIsSorted() === "asc" ? "↑" : "↓"}
                      </Box>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    sx={{
                      textAlign: cell.column.columnDef.align || "left",
                      fontFamily: cell.column.columnDef.isNumeric
                        ? "monospace"
                        : "inherit",
                      color:
                        cell.column.columnDef.getColor?.(cell.getValue()) ||
                        "inherit",
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const columnHelper = createColumnHelper();

  // Common SPAC columns configuration
  const commonColumns = [
    columnHelper.accessor("SYMBOL", {
      header: "Symbol",
      align: "left",
      size: 50,
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("current_price", {
      header: "Current Price",
      align: "right",
      isNumeric: true,
      cell: (info) => Number(info.getValue()).toFixed(2),
    }),
    columnHelper.accessor("LMV", {
      header: "LMV",
      align: "right",
      isNumeric: true,
      cell: (info) => formatCurrency(info.getValue()),
    }),
    columnHelper.accessor("risk_to_zero_bps_spread", {
      header: "Risk to Zero BPS Spread",
      align: "right",
      isNumeric: true,
      getColor: (value) => (Number(value) < 0 ? "#ef4444" : "#10b981"),
      cell: (info) => formatCurrency(info.getValue()),
    }),
    columnHelper.accessor("risk_to_50_bps_spread", {
      header: "Risk to 50 BPS Spread",
      align: "right",
      isNumeric: true,
      getColor: (value) => (Number(value) < 0 ? "#ef4444" : "#10b981"),
      cell: (info) => formatCurrency(info.getValue()),
    }),
    columnHelper.accessor("risk_to_100_bps_spread", {
      header: "Risk to 100 BPS Spread",
      align: "right",
      isNumeric: true,
      getColor: (value) => (Number(value) < 0 ? "#ef4444" : "#10b981"),
      cell: (info) => formatCurrency(info.getValue()),
    }),
  ];

  // Full columns configuration for warrants and rights
  const fullColumns = [
    columnHelper.accessor("SYMBOL", {
      header: "Symbol",
      align: "left",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("current_price", {
      header: "Current Price",
      align: "right",
      isNumeric: true,
      cell: (info) => Number(info.getValue()).toFixed(2),
    }),
    columnHelper.accessor("market_value", {
      header: "Market Value",
      align: "right",
      isNumeric: true,
      cell: (info) => formatCurrency(info.getValue()),
    }),
    columnHelper.accessor("risk_to_50pct_drop_in_wts_rts", {
      header: "Risk to 50% Drop in Wts & Rts",
      align: "right",
      isNumeric: true,
      getColor: (value) => (Number(value) < 0 ? "#ef4444" : "#10b981"),
      cell: (info) => formatCurrency(info.getValue()),
    }),
    columnHelper.accessor("risk_to_75pct_drop_in_wts_rts", {
      header: "Risk to 75% Drop in Wts & Rts",
      align: "right",
      isNumeric: true,
      getColor: (value) => (Number(value) < 0 ? "#ef4444" : "#10b981"),
      cell: (info) => formatCurrency(info.getValue()),
    }),
  ];

  return (
    <Box
      sx={{
        padding: "6px",
        backgroundColor: "#f8f9fa",
        minHeight: "calc(100vh - 130px)",
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "6px",
          padding: "16px 24px",
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          border: "1px solid rgba(0,0,0,0.05)",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "#1a237e",
            fontSize: "1.5rem",
            letterSpacing: "-0.5px",
          }}
        >
          SPAC Risk Dashboard
        </Typography>
        <ExportToCsvButton
          columns={commonColumns.map((col) => col.accessorKey)}
          data={spacRiskData}
          filename="SPAC_Risk_Data"
          theme={theme}
        />
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={1}>
        {/* Column 1 - Key Metrics */}
        <Grid item xs={12} md={2}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Card
                sx={{
                  backgroundColor: "#ffffff",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  borderRadius: "16px",
                  transition: "all 0.3s ease-in-out",
                  border: "1px solid rgba(0,0,0,0.05)",
                  "&:hover": {
                    boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent
                  sx={{
                    padding: "16px",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      marginBottom: "20px",
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      color: "#1a237e",
                      display: "flex",
                      alignItems: "center",
                      "&:before": {
                        content: '""',
                        display: "block",
                        width: "4px",
                        height: "24px",
                        backgroundColor: "#1a237e",
                        marginRight: "12px",
                        borderRadius: "2px",
                      },
                    }}
                  >
                    Risk Statistics
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <StatItem
                      label="LMV"
                      amount={summary.totalLMV}
                      percentage={(summary.totalLMV / NAV_NUMBER) * 100}
                      isNegative={false}
                    />
                    <StatItem
                      label="SMV"
                      amount={summary.totalSMV}
                      percentage={(summary.totalSMV / NAV_NUMBER) * 100}
                      isNegative={true}
                    />
                    <StatItem
                      label="Net MV"
                      amount={summary.netMarketValue}
                      percentage={(summary.netMarketValue / NAV_NUMBER) * 100}
                      isNegative={summary.netMarketValue < 0}
                    />
                    <StatItem
                      label="Risk to Zero BPs Spread"
                      amount={summary.totalRiskToZero}
                      percentage={(summary.totalRiskToZero / NAV_NUMBER) * 100}
                      isNegative={summary.totalRiskToZero < 0}
                    />
                    <StatItem
                      label="Risk to 50 BPs Spread"
                      amount={summary.totalRiskTo50}
                      percentage={(summary.totalRiskTo50 / NAV_NUMBER) * 100}
                      isNegative={summary.totalRiskTo50 < 0}
                    />
                    <StatItem
                      label="Risk to 100 BPs Spread"
                      amount={summary.totalRiskTo100}
                      percentage={(summary.totalRiskTo100 / NAV_NUMBER) * 100}
                      isNegative={summary.totalRiskTo100 < 0}
                    />
                    <StatItem
                      label="Risk to 50% Drop in Wts & RTS"
                      amount={summary.totalRiskTo50pctDrop}
                      percentage={
                        (summary.totalRiskTo50pctDrop / NAV_NUMBER) * 100
                      }
                      isNegative={summary.totalRiskTo50pctDrop < 0}
                    />
                    <StatItem
                      label="Risk to 75% Drop in Wts & RTS"
                      amount={summary.totalRiskTo75pctDrop}
                      percentage={
                        (summary.totalRiskTo75pctDrop / NAV_NUMBER) * 100
                      }
                      isNegative={summary.totalRiskTo75pctDrop < 0}
                    />
                    <StatItem
                      label="Weighted Avg Days to Liquidation (Projected CIT)"
                      amount={weightedAvgDaysToLiquidation}
                      suffix=" days"
                      hidePercentage
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Column 2 - SPAC Common Risk */}
        <Grid item xs={12} md={3.33}>
          <Card
            sx={{
              backgroundColor: "#ffffff",
              boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              borderRadius: "16px",
              height: "100%",
              transition: "all 0.3s ease-in-out",
              border: "1px solid rgba(0,0,0,0.05)",
            }}
          >
            <CardContent
              sx={{
                padding: "16px",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  marginBottom: "16px",
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  color: "#1a237e",
                  display: "flex",
                  alignItems: "center",
                  "&:before": {
                    content: '""',
                    display: "block",
                    width: "4px",
                    height: "24px",
                    backgroundColor: "#1a237e",
                    marginRight: "12px",
                    borderRadius: "2px",
                  },
                }}
              >
                SPAC Common Risk
              </Typography>
              <Box sx={{ flex: 1, overflow: "hidden" }}>
                <CustomTable
                  columns={commonColumns}
                  data={spacRiskData.filter(
                    (row) =>
                      row.Security_Type !== "WARRANT" &&
                      row.Security_Type !== "RIGHT"
                  )}
                  defaultSort={{ id: "risk_to_zero_bps_spread", desc: false }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Column 3 - SPAC Warrant Risk */}
        <Grid item xs={12} md={3.33}>
          <Card
            sx={{
              backgroundColor: "#ffffff",
              boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              borderRadius: "16px",
              height: "100%",
              transition: "all 0.3s ease-in-out",
              border: "1px solid rgba(0,0,0,0.05)",
            }}
          >
            <CardContent
              sx={{
                padding: "16px",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  marginBottom: "16px",
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  color: "#1a237e",
                  display: "flex",
                  alignItems: "center",
                  "&:before": {
                    content: '""',
                    display: "block",
                    width: "4px",
                    height: "24px",
                    backgroundColor: "#1a237e",
                    marginRight: "12px",
                    borderRadius: "2px",
                  },
                }}
              >
                SPAC Warrant Risk
              </Typography>
              <Box sx={{ flex: 1, overflow: "hidden" }}>
                <CustomTable
                  columns={fullColumns}
                  data={spacRiskData.filter(
                    (row) => row.Security_Type === "WARRANT"
                  )}
                  defaultSort={{
                    id: "risk_to_50pct_drop_in_wts_rts",
                    desc: false,
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Column 4 - SPAC Right Risk */}
        <Grid item xs={12} md={3.33}>
          <Card
            sx={{
              backgroundColor: "#ffffff",
              boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              borderRadius: "16px",
              height: "100%",
              transition: "all 0.3s ease-in-out",
              border: "1px solid rgba(0,0,0,0.05)",
            }}
          >
            <CardContent
              sx={{
                padding: "16px",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  marginBottom: "16px",
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  color: "#1a237e",
                  display: "flex",
                  alignItems: "center",
                  "&:before": {
                    content: '""',
                    display: "block",
                    width: "4px",
                    height: "24px",
                    backgroundColor: "#1a237e",
                    marginRight: "12px",
                    borderRadius: "2px",
                  },
                }}
              >
                SPAC Right Risk
              </Typography>
              <Box sx={{ flex: 1, overflow: "hidden" }}>
                <CustomTable
                  columns={fullColumns}
                  data={spacRiskData.filter(
                    (row) => row.Security_Type === "RIGHT"
                  )}
                  defaultSort={{
                    id: "risk_to_50pct_drop_in_wts_rts",
                    desc: false,
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SpacRisk;
